<template>
  <header class="pr-topbar">
    <div class="pr-crumb">
      Price Rescue / <b>{{ title }}</b>
    </div>
    <div class="pr-top-user">
      <button
        class="pr-notification-link"
        type="button"
        aria-label="Open notifications"
        @click="emit('openNotifications')"
      >
        <span class="pr-notification-icon" aria-hidden="true">!</span>
        <span>Notifications</span>
        <span class="pr-notification-count">{{ notifications.attentionUnreadCount }}</span>
      </button>
      <div class="pr-avatar">{{ userInitials }}</div>
      <div>
        <b>{{ displayName }}</b>
        <div class="pr-tiny pr-muted">{{ roleLabel }}</div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import { useMerchantSessionStore } from "../../stores/session-store.js";
import { useNotificationsStore } from "../../features/notifications/store.js";

const route = useRoute();
const session = useMerchantSessionStore();
const notifications = useNotificationsStore();
const emit = defineEmits<{
  openNotifications: [];
}>();

const title = computed(() => String(route.meta["title"] ?? "Dashboard"));
const displayName = computed(() => {
  const user = session.sessionContext?.user;
  if (isDisplayableUserName(user?.displayName)) {
    return user.displayName;
  }

  if (isDisplayableUserName(user?.email)) {
    return user.email;
  }

  return roleFallbackLabel(user?.role);
});
const roleLabel = computed(() => session.sessionContext?.user.role ?? "Merchant");
const userInitials = computed(
  () =>
    displayName.value
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.slice(0, 1).toUpperCase())
      .join("") || "M",
);

function isDisplayableUserName(value: string | undefined): value is string {
  if (value === undefined) return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (trimmed === "Shopify merchant") return false;
  if (/^\d+$/u.test(trimmed)) return false;
  if (/^gid:\/\/shopify\/user\//iu.test(trimmed)) return false;
  return true;
}

function roleFallbackLabel(role: string | undefined): string {
  if (role === "OWNER") return "Store owner";
  if (role === "APP_ADMIN") return "App admin";
  return "Merchant user";
}
</script>
